import { useEffect, useRef, useCallback, useState } from 'react';

type UsePlayAudioParams = {
    isLoop?: boolean;
    isIncreasing?: boolean;
    deviceId?: string;
    volume?: number;
};

class AudioCancelError extends Error {}

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, ms);
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timeout);
                reject(new AudioCancelError('Aborted'));
            },
            {
                once: true,
            },
        ); // { once: true } гарантирует, что abort-листенер снимается сразу после срабатывания и не висит на сигнале.
    });

const applySinkId = (
    audio: HTMLAudioElement & { setSinkId?: (sinkId: string) => Promise<void> },
    deviceId?: string,
) => {
    if (!deviceId) return;
    if (typeof audio.setSinkId !== 'function') return;
    audio.setSinkId(deviceId).catch((err: unknown) => {
        const name = err instanceof Error ? err.name : 'Unknown';
        // Это просто страховка на случай, если разрешения на проигрывание ещё нет
        if (name === 'NotAllowedError' || name === 'NotFoundError') return;
        console.warn(`Не удалось переключить устройство вывода (${name}):`, err);
    });
};

/**
 * Хук для воспроизведения аудио с поддержкой плавного нарастания громкости (fade-in).
 *
 * @param src     - URL аудиофайла. Смена src пересоздаёт Audio-объект.
 * @param params  - isLoop: зацикливать воспроизведение; isIncreasing: нарастание громкости при play().
 * @returns       play()  — начать воспроизведение
 *                stop()  — остановить и перемотать в начало
 *                isPlay  — true, пока аудио играет
 */
export const usePlayAudio = (src: string, params?: UsePlayAudioParams) => {
    const { isLoop = false, isIncreasing = false, deviceId, volume = 1 } = params ?? {};

    const audioRef = useRef<HTMLAudioElement | null>(null);
    // Контроллер для отмены текущего fade-in при повторном вызове play() или stop()
    const abortRef = useRef<AbortController | null>(null);
    const [isPlay, setIsPlay] = useState(false);

    // Нужно, чтобы создание Audio не зависел от outputDeviceId
    // иначе смена устройства пересоздавала бы
    // Audio-элемент и обрывала воспроизведение.
    const outputDeviceIdRef = useRef(deviceId);
    outputDeviceIdRef.current = deviceId;

    // Нужно, чтобы создание Audio не зависел от volume
    // иначе смена устройства пересоздавала бы
    // Audio-элемент и обрывала воспроизведение.
    const volumeRef = useRef(volume);
    volumeRef.current = volume;

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || audio.paused) return;
        audio.volume = volume;
    }, [volume]);

    useEffect(() => {
        if (!src) return;

        const audio = new Audio(src);
        audio.loop = isLoop;
        audioRef.current = audio;

        // Отслеживаем состояние через нативные события элемента, а не вручную в play()/stop().
        // Это охватывает все случаи остановки: явный stop(), окончание трека, браузерный autoplay-блок.
        const handlePlay = () => setIsPlay(true);
        const handleStop = () => setIsPlay(false);

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handleStop);
        audio.addEventListener('ended', handleStop);

        return () => {
            // Останавливаем и освобождаем ресурсы браузера: src = '' + load() сигнализируют движку,
            // что можно выгрузить буферы и закрыть сетевое соединение.
            audio.pause();
            audio.currentTime = 0;
            //audio.src = '' само по себе не всегда освобождает сетевые ресурсы — браузер может держать соединение открытым или
            //буферы в памяти. load() после обнуления src явно сигнализирует движку сбросить состояние загрузки и выгрузить буферы.
            //Без него особенно на мобильных браузерах аудио-объект может продолжать занимать память и держать соединение до GC.
            audio.src = '';
            audio.load();
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handleStop);
            audio.removeEventListener('ended', handleStop);
            audioRef.current = null;
            // Прерываем fade-in, если он шёл в момент смены src или размонтирования
            abortRef.current?.abort();
            abortRef.current = null;
            setIsPlay(false);
        };
    }, [src, isLoop]);

    // Передёргиваем sinkId на существующем элементе. Применяем и к paused элементу, поэтому проигрывание не прерывается:
    // звук переключается на новое устройство и продолжается с того же места.
    useEffect(() => {
        if (!deviceId) return;
        const audio = audioRef.current;
        if (!audio) return;
        applySinkId(audio, deviceId);
    }, [deviceId]);

    // Плавное нарастание громкости поэтапно.
    const fadeInVolume = useCallback(async (signal: AbortSignal) => {
        const steps = [
            {
                duration: 1000,
                volume: 0.1,
            },
            {
                duration: 1000,
                volume: 0.2,
            },
            {
                duration: 2000,
                volume: 0.4,
            },
            {
                duration: 2000,
                volume: 0.7,
            },
            {
                duration: 0,
                volume: 0.9,
            },
        ];

        try {
            for (const step of steps) {
                if (audioRef.current) {
                    audioRef.current.volume = step.volume;
                }
                await sleep(step.duration, signal);
            }
        } catch (err) {
            if (!(err instanceof AudioCancelError)) {
                console.warn('Ошибка в fadeInVolume:', err);
            }
        }
    }, []);

    const play = useCallback(() => {
        if (!audioRef.current) return;

        // Прерываем предыдущий fade-in перед запуском нового, чтобы не было гонки двух циклов
        abortRef.current?.abort();
        abortRef.current = new AbortController();

        audioRef.current.volume = isIncreasing ? 0.1 : volumeRef.current;
        audioRef.current.muted = false;
        if (outputDeviceIdRef.current) {
            applySinkId(audioRef.current, outputDeviceIdRef.current);
        }
        audioRef.current.play().catch(err => {
            console.warn('Ошибка воспроизведения аудио:', err);
        });

        if (isIncreasing) {
            fadeInVolume(abortRef.current.signal);
        }
    }, [isIncreasing, fadeInVolume]);

    const stop = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, []);

    return {
        play,
        stop,
        isPlay,
    };
};
